import type { PublicDetailsView } from '@discovr/features';
import type { Campaign } from '@discovr/types';

/**
 * Assembles a partial-but-Campaign-shaped object from the public share-view
 * payload, so `CampaignDetailsCards` (100% presentational — see its own doc
 * comment) can be reused UNMODIFIED for an anonymous visitor exactly as it
 * renders for an authed one. Every field it actually reads is real, mapped
 * data; every field on `Campaign` it never reads (id, brand_id,
 * spend_to_date, creators_count, created_at, updated_at, brief_locked, …)
 * gets an inert placeholder purely to satisfy the type — the BE's
 * `publicDetailsView` DTO never sends them (see that Go type's doc comment
 * for why), and nothing here or downstream reads them back.
 */
export function publicDetailsToCampaign(details: PublicDetailsView): Campaign {
  return {
    id: '',
    brand_id: '',
    name: details.name,
    description: details.description ?? '',
    platform: details.platform,
    primary_goal: details.primary_goal ?? '',
    total_budget: details.total_budget,
    spend_to_date: 0,
    payout_model: details.payout_model ?? '',
    deliverables: details.deliverables ?? [],
    deliverables_text: details.deliverables_text ?? '',
    usage_rights: details.usage_rights_days ? { days: details.usage_rights_days } : undefined,
    status: details.status,
    creator_preference: {
      creator_count: details.creator_preference.creator_count,
      categories: details.creator_preference.categories ?? [],
      tiers: details.creator_preference.tiers ?? [],
      creator_genders: details.creator_preference.creator_genders,
      gender_notes: details.creator_preference.gender_notes,
    },
    targeting: {
      demographics: {
        age_min: details.demographics.age_min ?? 0,
        age_max: details.demographics.age_max ?? 0,
        genders: details.demographics.genders,
        languages: details.demographics.languages,
        regions: details.demographics.regions,
      },
    },
    brief: {
      primary_focus: details.brief.primary_focus ?? '',
      video_title: details.brief.video_title ?? '',
      video_description: details.brief.video_description ?? '',
      secondary_focus: details.brief.secondary_focus ?? '',
      cta: details.brief.cta ?? '',
      dos: details.brief.dos ?? null,
      donts: details.brief.donts ?? null,
      sample_video_url: details.brief.sample_video_url ?? '',
    },
    // product_id is required on Campaign['products'] entries (it keys the
    // authed edit form's stable rows) but never sent by publicDetailsProduct
    // — nothing on the public read-only path needs it, an index-derived
    // placeholder satisfies the type without meaning anything.
    products: (details.products ?? []).map((p, i) => ({
      product_id: `public-${i}`,
      name: p.name,
      product_url: p.product_url,
      notes: p.notes,
    })),
    creators_count: 0,
    created_at: '',
    updated_at: '',
    brief_locked: false,
  };
}
